import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CurrentOrPreviousGroup, Member } from '../../../../../api/groups';
import { Module } from '../../../../../api/module';
import { ScorableItem } from '../../api/scorable-items';
import {
  useFilterInLocalStorage,
  useFilterInLocalStorageSynchronizer,
} from '../../components/per-student/hooks/useFilterInLocalStorage';
import { FilteredItem } from '../../components/scorable-item-filter-modal/ScorableItemFilterModal';
import { ResultDataContextType, SchoolYearValue, useResultsDataContext } from '../../context/ResultsDataContext';
import { useSelectedGroups } from './hooks/useSelectedGroups';
import { useSelectedMembers } from './hooks/useSelectedMembers';
import { useSelectedModule } from './hooks/useSelectedModule';
import { useSelectedSchool } from './hooks/useSelectedSchool';
import { useSelectedSchoolYear } from './hooks/useSelectedSchoolYear';
import { FilterSearchParam } from '../../../../../shared-components/elementis/types/FilterSearchParam';

export const FilterContext = createContext<
  | {
      selectedSchoolYear: SchoolYearValue | null;
      setSelectedSchoolYear: (schoolYear: SchoolYearValue) => void;
      selectedModule: Module | null;
      setSelectedModule: (module: Module) => void;
      selectedSchool: CurrentOrPreviousGroup['school'] | null;
      setSelectedSchool: (school: CurrentOrPreviousGroup['school']) => void;
      selectedGroups: CurrentOrPreviousGroup[];
      setSelectedGroups: (groups: CurrentOrPreviousGroup[]) => void;
      selectedMembers: Record<string, Member[]>;
      setSelectedMembers: (members: string[]) => void;
      selectedTableItems: FilteredItem[];
      setSelectedTableItems: (items: FilteredItem[]) => void;
      exerciseIds: string[];
      setExerciseIds: (exerciseIds: string[]) => void;
      clearFilters: () => void;
    }
  | undefined
>(undefined);

function determineInitialFilters(
  schoolYears: ResultDataContextType['schoolYears'],
  modules: Module[],
  scorableItems: Record<string, Record<string, ScorableItem[]>>,
  groups: CurrentOrPreviousGroup[],
) {
  for (const schoolYear of [schoolYears.current, schoolYears.previous]) {
    for (const module of modules) {
      if (scorableItems[schoolYear.id]?.[module.id]?.length) {
        const groupsOfSchoolYear = groups.filter(group => group.schoolYear === schoolYear.id);

        if (groupsOfSchoolYear.length) {
          return {
            schoolYear,
            module,
            groups: groupsOfSchoolYear,
            school: groupsOfSchoolYear[0]?.school,
          };
        }
      }
    }
  }

  const hasGroupsInCurrentSchoolYear = groups.some(g => g.schoolYear === schoolYears.current.id);

  const initialGroups = hasGroupsInCurrentSchoolYear
    ? groups.filter(x => x.schoolYear === schoolYears.current.id)
    : groups.filter(x => x.schoolYear === schoolYears.previous.id);

  return {
    schoolYear: hasGroupsInCurrentSchoolYear ? schoolYears.current : schoolYears.previous,
    module: modules[0],
    groups: initialGroups,
    school: initialGroups[0]?.school,
  };
}

export function FilterContextProvider({
  children,
  selectedTab,
}: {
  children: React.ReactNode;
  selectedTab: 'STUDENT' | 'EXERCISE';
}) {
  const { schoolYears, modules, scorableItemsPerSchoolYearPerModule, groups, loading, members } =
    useResultsDataContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const setDefaultFilters = useCallback(
    (reset?: boolean) => {
      if (loading.groups || !modules.length || !groups.length) return;

      const defaultFilters = determineInitialFilters(schoolYears, modules, scorableItemsPerSchoolYearPerModule, groups);

      const groupsToSelect = selectedTab === 'STUDENT' ? [defaultFilters.groups[0]] : defaultFilters.groups;

      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set(FilterSearchParam.Groups, groupsToSelect.map(g => g.id).join(','));
        newParams.set(FilterSearchParam.SchoolYear, defaultFilters.schoolYear.id);
        newParams.set(FilterSearchParam.School, defaultFilters.school?.id);

        if (reset) {
          newParams.delete(FilterSearchParam.Members);
          newParams.delete(FilterSearchParam.Exercises);
        }

        return newParams;
      });
    },
    [groups, loading.groups, modules, schoolYears, scorableItemsPerSchoolYearPerModule, selectedTab, setSearchParams],
  );

  const [selectedModule, setSelectedModule] = useSelectedModule();
  const [selectedSchoolYear, setSelectedSchoolYear] = useSelectedSchoolYear({ selectedTab });
  const [selectedSchool, setSelectedSchool] = useSelectedSchool({ selectedTab, selectedSchoolYear });
  const [selectedGroups, setSelectedGroups] = useSelectedGroups({
    selectedTab,
    selectedSchoolYear,
    selectedSchool,
    setDefaultFilters,
  });
  const [selectedMembers, setSelectedMembers] = useSelectedMembers({ selectedGroups, selectedTab });
  const exerciseIds = searchParams.get(FilterSearchParam.Exercises)?.split(',') || [];

  const setExerciseIds = useCallback(
    (exerciseIds: string[]) => {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set(FilterSearchParam.Exercises, exerciseIds.join(','));
        return newParams;
      });
    },
    [setSearchParams],
  );

  const superModuleId = modules[0]?.id;

  useFilterInLocalStorageSynchronizer(
    {
      moduleId: selectedModule?.id,
      schoolYearId: selectedSchoolYear?.id,
      schoolId: selectedSchool?.id,
      groupIds: selectedGroups.map(group => group.id),
      selectedTab,
    },
    superModuleId,
  );

  const { getStoredData } = useFilterInLocalStorage(superModuleId);

  const [selectedTableItems, setSelectedTableItems] = useState<FilteredItem[]>([]);

  useEffect(() => {
    if (selectedModule && selectedSchoolYear) return;
    if (loading.groups) return;

    if (modules.length > 0) {
      const initialFilters = determineInitialFilters(schoolYears, modules, scorableItemsPerSchoolYearPerModule, groups);

      const hasLocalStorageData = getStoredData() !== null;

      if (initialFilters && !hasLocalStorageData) {
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);

          if (!selectedModule) {
            newParams.set(FilterSearchParam.Module, initialFilters.module.id);
          }

          if (!selectedSchoolYear) {
            newParams.set(FilterSearchParam.SchoolYear, initialFilters.schoolYear.id);
          }

          if (!selectedSchool && initialFilters.school) {
            newParams.set(FilterSearchParam.School, initialFilters.school.id);
          }

          if (!selectedGroups.length) {
            const groupIdsToSelect =
              selectedTab === 'STUDENT' ? initialFilters.groups[0]?.id : initialFilters.groups.join(',');
            newParams.set(FilterSearchParam.Groups, groupIdsToSelect);
          }

          return newParams;
        });
      }
    }
  }, [
    schoolYears,
    modules,
    scorableItemsPerSchoolYearPerModule,
    selectedModule,
    selectedSchoolYear,
    setSearchParams,
    groups,
    selectedSchool,
    loading.groups,
    members,
    selectedGroups.length,
    searchParams,
    selectedTab,
    getStoredData,
  ]);

  function clearFilters() {
    setDefaultFilters(true);
  }

  return (
    <FilterContext.Provider
      value={{
        selectedSchoolYear,
        setSelectedSchoolYear,
        selectedModule,
        setSelectedModule,
        selectedSchool,
        setSelectedSchool,
        selectedGroups,
        setSelectedGroups,
        selectedMembers,
        setSelectedMembers,
        selectedTableItems,
        setSelectedTableItems,
        exerciseIds,
        setExerciseIds,
        clearFilters,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
}

export function useFilters() {
  const context = useContext(FilterContext);

  if (!context) {
    throw new Error('useFilters must be used within a FilterContextProvider');
  }

  return context;
}
